/**
 * @fileoverview Auth utilities backed by localStorage JWT storage.
 */

import { type JwtPayload } from '@schema/index';
import { sessionAuth, storeSession, clearSession, type CurrentUser } from '@/api';
import { getErrorMessage } from '@/shared/utils';

// Re-export the user type so consumers don't need to import from sessionConfig directly
export type User = CurrentUser;

export interface AuthResult {
  success: boolean;
  user: CurrentUser | null;
  error: string | null;
}

function decodeJwtPayload(token: string): JwtPayload {
  const parts = token.split('.');
  if (parts.length !== 3) throw new Error('Invalid token format');
  const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
  return JSON.parse(atob(base64)) as JwtPayload;
}

/** Sign in by storing a backend-issued JWT. */
export async function signInWithJwt(token: string): Promise<AuthResult> {
  try {
    const payload = decodeJwtPayload(token);
    storeSession({
      token,
      user: {
        uid: payload.sub,
        name: payload.name ?? '',
        email: payload.email ?? '',
        picture: payload.picture ?? '',
        github_login: payload.github_login ?? '',
      },
    });
    return { success: true, user: sessionAuth.currentUser, error: null };
  } catch (error) {
    return {
      success: false,
      user: null,
      error: getErrorMessage(error, 'Failed to sign in'),
    };
  }
}

/** Clear the stored session. Pairs with `signInWithJwt`. */
export async function signOut(): Promise<AuthResult> {
  try {
    clearSession();
    return { success: true, user: null, error: null };
  } catch (error) {
    return {
      success: false,
      user: sessionAuth.currentUser,
      error: getErrorMessage(error, 'Failed to sign out'),
    };
  }
}

export function onAuthStateChanged(callback: (user: CurrentUser | null) => void): () => void {
  return sessionAuth.onAuthStateChanged(callback);
}
